import { useRouter } from 'next/router';
import * as qs from 'query-string';
import { useCallback, useMemo, useState } from 'react';

import {
  isExistsEmail,
  signInWithEmailUser,
  signUpWithEmailAndPassword,
} from '~/firebase/fbAuth';
import { SIGN_CALLBACK } from '~/utils/constants';
import { authErrorHandler } from '~/utils/error-handler';
import { logger } from '~/utils/loggerUtil';

export const useSign = () => {
  // 送信中
  const [isSubmittingSignIn, setIsSubmittingSignIn] = useState(false);
  const [isSubmittingSignUp, setIsSubmittingSignUp] = useState(false);
  const router = useRouter();
  const signCallback = router.query[SIGN_CALLBACK];

  const isSubmitting = useMemo(() => {
    return isSubmittingSignIn || isSubmittingSignUp;
  }, [isSubmittingSignIn, isSubmittingSignUp]);

  // ログイン/会員登録後にコールバックURLがある場合にリダイレクトする
  const redirectToCallback = useCallback(() => {
    if (typeof signCallback === 'string' && signCallback !== '') {
      return router.push(
        qs.stringifyUrl({
          url: encodeURI(signCallback),
          query: { mode: router.query.mode },
        })
      );
    } else {
      return router.push('/');
    }
  }, [router, signCallback]);

  // ログインメソッド
  const signIn = async (email: string, password: string) => {
    setIsSubmittingSignIn(true);
    // 存在しないメールアドレスのとき
    try {
      const isExists = await isExistsEmail(email);
      if (!isExists) {
        alert('存在しないメールアドレスです');
        setIsSubmittingSignIn(false);
        return Promise.reject();
      }
    } catch (error) {
      alert(authErrorHandler(error));
      setIsSubmittingSignIn(false);
      return Promise.reject();
    }

    try {
      // ログイン
      await signInWithEmailUser(email, password);
      setIsSubmittingSignIn(false);
      return Promise.resolve();
    } catch (error) {
      logger.error(error);
      setIsSubmittingSignIn(false);
      alert(authErrorHandler(error));
      return Promise.reject();
    }
  };

  // 会員登録メソッド
  const signUp = async (email: string, password: string) => {
    setIsSubmittingSignUp(true);
    // 存在しないメールアドレスのとき
    try {
      const isExists = await isExistsEmail(email);
      if (isExists) {
        setIsSubmittingSignUp(false);
        alert('登録されているメールアドレスです');
        return Promise.reject();
      }
    } catch (error) {
      setIsSubmittingSignUp(false);
      alert(authErrorHandler(error));
      return Promise.reject();
    }

    try {
      // 会員登録
      await signUpWithEmailAndPassword(email, password);
      setIsSubmittingSignUp(false);
      return Promise.resolve();
    } catch (error) {
      setIsSubmittingSignUp(false);
      logger.error(error);
      alert(authErrorHandler(error));
      return Promise.reject();
    }
  };

  return {
    signIn,
    signUp,
    redirectToCallback,
    isSubmitting,
    isSubmittingSignIn,
    isSubmittingSignUp,
  };
};
